import { useState } from 'react';

import { CompletePlaceholderDialog } from '../dialogs/complete-placeholder-dialog';
import { CorrectInvoiceDialog } from '../dialogs/correct-invoice-dialog';
import { CreateNextDialog } from '../dialogs/create-next-dialog';
import type {
  BlContextForTradeInvoice,
  TicketContextForTradeInvoice,
} from '../dialogs/trade-invoice-payload';
import { invoiceVerbFor } from '../lib/invoice-actions';
import { resolveNoteKind } from '../lib/invoice-label';
import type { InvoiceRow } from '../types';
import { normalizeTradeType } from './bl-invoice-trade-dialog';
import { RowEditDialog, type RowDialogKind } from './bl-invoice-edit-dialog';
import { RowTriggers } from './bl-invoice-row-triggers';

import type { InvoiceActionKey } from '../lib/invoice-actions';

type NoteType = 'Credit' | 'Debit';

export interface BlInvoiceRowActionsProps {
  readonly invoice: InvoiceRow;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly onChanged?: () => void;
}

// Which edit dialog a row opens — shared with the detail drawer so both
// surfaces classify an invoice identically.
export function classifyRow(invoice: InvoiceRow): RowDialogKind | null {
  const trade = normalizeTradeType(invoice.invoice_type);
  if (trade !== null) return { kind: 'trade', type: trade };
  if (invoice.invoice_type === 'Advance') return { kind: 'advance' };
  const noteKind = resolveNoteKind(invoice);
  if (noteKind !== null) {
    const noteType: NoteType = noteKind === 'Debit' ? 'Debit' : 'Credit';
    return { kind: 'adjustment', noteType };
  }
  return null;
}

function noop(): void {
  /* no-op */
}

// The row renders the consolidated verbs from the shared model: Change values
// (state-routed over edit / correct / complete-placeholder), New follow-on,
// and Discard. A classifiable row always shows the group so the affordances
// are discoverable; the edit route additionally needs the row to classify.
export function BlInvoiceRowActions(props: BlInvoiceRowActionsProps) {
  const [changeOpen, setChangeOpen] = useState(false);
  const [createNextOpen, setCreateNextOpen] = useState(false);
  const kind = classifyRow(props.invoice);
  const changeVerb = invoiceVerbFor(props.invoice, 'change_values');
  const changeRoute: InvoiceActionKey | null =
    changeVerb === null || (changeVerb.routesTo === 'edit' && kind === null)
      ? null
      : changeVerb.routesTo;
  const creatable = invoiceVerbFor(props.invoice, 'create_next') !== null;
  if (kind === null && !changeOpen && !createNextOpen) return null;
  const onChanged = props.onChanged ?? noop;
  return (
    <div className="flex items-center gap-2">
      <RowTriggers
        invoice={props.invoice}
        changeRoute={changeRoute}
        creatable={creatable}
        onChangeValues={() => {
          setChangeOpen(true);
        }}
        onCreateNext={() => {
          setCreateNextOpen(true);
        }}
        onDiscarded={onChanged}
      />
      <RowChangeValuesDialog
        route={changeRoute}
        kind={kind}
        props={props}
        open={changeOpen}
        onOpenChange={setChangeOpen}
        onChanged={onChanged}
      />
      {creatable && (
        <CreateNextDialog
          open={createNextOpen}
          onOpenChange={setCreateNextOpen}
          invoice={props.invoice}
          side={props.side}
          orderId={props.orderId}
          blOrderId={props.blOrderId}
          blOrderName={props.bl.blOrderName}
          onCreated={onChanged}
        />
      )}
    </div>
  );
}

function RowChangeValuesDialog({
  route,
  kind,
  props,
  open,
  onOpenChange,
  onChanged,
}: {
  readonly route: InvoiceActionKey | null;
  readonly kind: RowDialogKind | null;
  readonly props: BlInvoiceRowActionsProps;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onChanged: () => void;
}) {
  if (route === 'complete_placeholder') {
    return (
      <CompletePlaceholderDialog
        open={open}
        onOpenChange={onOpenChange}
        invoiceId={props.invoice.id}
      />
    );
  }
  if (route === 'correct') {
    return (
      <CorrectInvoiceDialog
        open={open}
        onOpenChange={onOpenChange}
        invoice={props.invoice}
        onCorrected={onChanged}
      />
    );
  }
  if (route !== 'edit' || kind === null) return null;
  return (
    <RowEditDialog
      kind={kind}
      invoice={props.invoice}
      side={props.side}
      orderId={props.orderId}
      blOrderId={props.blOrderId}
      ticket={props.ticket}
      bl={props.bl}
      open={open}
      onOpenChange={onOpenChange}
      onSaved={onChanged}
    />
  );
}
